import type { TextProps as BaseTextProps } from "tamagui";
import { Text as BaseText } from "tamagui";

import { TextAttributesMapper } from "@/ui/core/Text";
import { ColorToken } from "@/ui/theme/tokens";

type RichTextVariant = "body" | "caption";

/**
 * Renders inline text with bold segments using markdown-style **bold** markers.
 * Only supports variants with regular/medium pairs (body, caption).
 *
 * @example
 * <RichText
 *   testID="welcome"
 *   text="There's **nothing you need to do right now** — we'll let you know."
 * />
 */
interface RichTextProps extends BaseViewProps {
  color?: BaseTextProps["color"];
  numberOfLines?: BaseTextProps["numberOfLines"];
  text: string;
  textAlign?: BaseTextProps["textAlign"];
  variant?: RichTextVariant;
}

interface TextSegment {
  isBold: boolean;
  text: string;
}

const BoldMarker = "**";

const parseSegments = (input: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  const parts = input.split(BoldMarker);

  // eslint-disable-next-line max-params
  parts.forEach((part, index) => {
    if (part === "") return;
    segments.push({ isBold: index % 2 === 1, text: part });
  });

  return segments;
};

const RichText = ({
  accessibilityLabel,
  color = ColorToken.NeutralPrimary,
  numberOfLines,
  testID,
  text,
  textAlign,
  variant = "body",
}: RichTextProps) => {
  const baseAttributes = TextAttributesMapper[variant];
  const boldAttributes = TextAttributesMapper[`${variant}Medium`];

  const segments = parseSegments(text);

  return (
    <BaseText
      aria-label={accessibilityLabel}
      color={color}
      fontFamily={baseAttributes.fontFamily}
      fontSize={baseAttributes.fontSize}
      letterSpacing={baseAttributes.fontSize * baseAttributes.letterSpacing}
      lineHeight={baseAttributes.lineHeight * baseAttributes.fontSize}
      maxFontSizeMultiplier={baseAttributes.maxFontSizeMultiplier}
      numberOfLines={numberOfLines}
      testID={testID}
      textAlign={textAlign}
    >
      {
        // eslint-disable-next-line max-params
        segments.map((segment, index) =>
          segment.isBold ? (
            <BaseText
              color={color}
              fontFamily={boldAttributes.fontFamily}
              fontSize={boldAttributes.fontSize}
              // eslint-disable-next-line react/no-array-index-key
              key={`${index}-${segment.text.slice(0, 8)}`}
              letterSpacing={
                boldAttributes.fontSize * boldAttributes.letterSpacing
              }
            >
              {segment.text}
            </BaseText>
          ) : (
            segment.text
          ),
        )
      }
    </BaseText>
  );
};

export { parseSegments };
export type { RichTextProps };
export default RichText;
